// TES-1019 (Emelie, points 9 and 12) — what the patient-memory panel shows
// about each row's origin, and its history: the earlier versions of a
// corrected row and the rows that were inactivated.
//
// Pure on purpose: no React, no path aliases. The unit tests in
// `apps/admin/test` run it with plain `node --test`.
import {
  COACH_MEMORY_CATEGORY_LABELS,
  COACH_MEMORY_FACT_TYPE_LABELS,
  coachMemorySourceLabel,
  type CoachMemoryItem,
} from "@care-by-ella/contracts";

export interface ProvenanceField {
  label: string;
  value: string;
}

/**
 * The provenance fields, in Emelie's order: area, type, source, conveyed by,
 * registered by, time. A row no human registered reads as created
 * automatically; conveyed-by appears only when someone conveyed it.
 */
export function provenanceFields(item: CoachMemoryItem): ProvenanceField[] {
  const fields: ProvenanceField[] = [
    { label: "Område", value: COACH_MEMORY_CATEGORY_LABELS[item.category] },
    { label: "Typ", value: COACH_MEMORY_FACT_TYPE_LABELS[item.factType] },
    { label: "Källa", value: coachMemorySourceLabel(item) },
  ];
  if (item.conveyedByEmployeeId) {
    fields.push({
      label: "Förmedlat av",
      value: item.conveyedByDisplayName ?? "Namn saknas",
    });
  }
  fields.push({
    label: "Registrerat av",
    value: item.createdByEmployeeId
      ? (item.createdByDisplayName ?? "Namn saknas")
      : "Skapad automatiskt",
  });
  fields.push({
    label: item.sourceType === "form_submission" ? "Svarsdatum" : "Tidpunkt",
    value: item.observedAt.slice(0, 10),
  });
  return fields;
}

/**
 * The versions a row replaced, newest first: walk `supersedesId` back
 * through the history. Stops on a missing link or a loop.
 */
export function previousVersions(
  item: CoachMemoryItem,
  history: readonly CoachMemoryItem[],
): CoachMemoryItem[] {
  const byId = new Map(history.map((h) => [h.id, h]));
  const out: CoachMemoryItem[] = [];
  const seen = new Set<string>([item.id]);
  let next = item.supersedesId ? byId.get(item.supersedesId) : undefined;
  while (next && !seen.has(next.id)) {
    out.push(next);
    seen.add(next.id);
    next = next.supersedesId ? byId.get(next.supersedesId) : undefined;
  }
  return out;
}

/**
 * The rows switched off rather than replaced: inactivated, answered, or
 * removed with their source. Superseded rows are not listed here; they are
 * the earlier versions of whichever row replaced them.
 */
export function retiredItems(
  history: readonly CoachMemoryItem[],
): CoachMemoryItem[] {
  return history.filter((h) => h.lifecycle !== "superseded");
}

export const LIFECYCLE_LABELS: Record<CoachMemoryItem["lifecycle"], string> = {
  active: "Aktiv",
  superseded: "Ersatt av en rättelse",
  completed: "Besvarad",
  inactive: "Inaktiverad",
  deleted: "Borttagen med källan",
  // TES-1367 — restricted on the patient's request; the text is not shown.
  restricted: "Begränsad på patientens begäran",
};

const MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "maj",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
];

function parts(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split("-").map(Number);
  return { y, m, d };
}

/**
 * TES-1192 — a temporary context's period the way the ticket writes it:
 * "3–17 okt", "28 sep–5 okt", and with years only when the period crosses
 * a new year ("28 dec 2026–3 jan 2027"). An open start reads "till 17 okt".
 */
export function formatPeriod(
  validFrom: string | null | undefined,
  validUntil: string | null | undefined,
): string {
  if (!validUntil) return validFrom ? `från ${formatDay(validFrom)}` : "";
  const to = parts(validUntil);
  if (!validFrom) return `till ${to.d} ${MONTHS[to.m - 1]}`;
  const from = parts(validFrom);
  if (from.y !== to.y) {
    return `${from.d} ${MONTHS[from.m - 1]} ${from.y}–${to.d} ${MONTHS[to.m - 1]} ${to.y}`;
  }
  if (from.m !== to.m) {
    return `${from.d} ${MONTHS[from.m - 1]}–${to.d} ${MONTHS[to.m - 1]}`;
  }
  return `${from.d}–${to.d} ${MONTHS[to.m - 1]}`;
}

function formatDay(iso: string): string {
  const { d, m } = parts(iso);
  return `${d} ${MONTHS[m - 1]}`;
}

/**
 * TES-1192 — the heading a history row carries. An expired temporary
 * context is still `active` (expiry is a read rule), so its lifecycle label
 * would read "Aktiv"; it reads "Tillfällig kontext 3–17 okt" instead.
 */
export function historyHeading(item: CoachMemoryItem): string {
  if (item.temporal === "expired") {
    return `Tillfällig kontext ${formatPeriod(item.validFrom, item.validUntil)}`;
  }
  return LIFECYCLE_LABELS[item.lifecycle];
}

/**
 * TES-1192 — what a temporary context carries beyond its text: the period,
 * the impact on daily treatment life and an agreement, each only when set.
 */
export function temporaryContextFields(
  item: CoachMemoryItem,
): ProvenanceField[] {
  if (item.memoryClass !== "temporary_context") return [];
  const fields: ProvenanceField[] = [
    { label: "Period", value: formatPeriod(item.validFrom, item.validUntil) },
  ];
  if (item.impact) {
    fields.push({
      label: "Påverkan på behandlingsvardagen",
      value: item.impact,
    });
  }
  if (item.agreement) {
    fields.push({ label: "Överenskommelse", value: item.agreement });
  }
  return fields;
}
